/**
 * Canvas rendering of the real stitch sequence (the same data that is exported).
 * Paths are built once per sequence in mm and drawn with a view transform.
 */
import { CMD, type StitchSequence } from '../../engine/stitch/sequence';
import type { Viewport } from '../../state/store';

export const THREAD_WIDTH_MM = 0.38;
export const FABRIC = '#efeae0';

export interface StitchPaths {
  /** One path per colour block (stitches only). */
  blocks: { color: string; path: Path2D }[];
  /** Jump moves (no penetration). */
  jumps: Path2D;
  trims: { x: number; y: number }[];
  colorChanges: { x: number; y: number }[];
}

/**
 * Builds drawable paths for stitches [0, upTo). Objects whose index is in `hidden` are skipped
 * (editor visibility only — never affects export).
 */
export function buildStitchPaths(seq: StitchSequence, upTo = seq.count, hidden: Set<number> | null = null): StitchPaths {
  const blocks = seq.threads.map((t) => ({ color: t.hex, path: new Path2D() }));
  const jumps = new Path2D();
  const trims: StitchPaths['trims'] = [];
  const colorChanges: StitchPaths['colorChanges'] = [];
  let prevX = 0, prevY = 0;
  let prevStitch = false;
  let started = false;
  const n = Math.min(upTo, seq.count);
  for (let i = 0; i < n; i++) {
    const c = seq.cmds[i];
    const x = seq.xs[i], y = seq.ys[i];
    if (c === CMD.STITCH) {
      const skip = hidden !== null && hidden.has(seq.objs[i]);
      const b = blocks[seq.blocks[i]];
      if (b && !skip) {
        if (prevStitch) {
          b.path.moveTo(prevX, prevY);
          b.path.lineTo(x, y);
        } else {
          // a penetration right after a jump/trim: draw a dot-sized stub so single stitches stay visible
          b.path.moveTo(x, y);
          b.path.lineTo(x + 1e-3, y);
        }
      }
      prevStitch = true;
    } else if (c === CMD.JUMP) {
      if (started) {
        jumps.moveTo(prevX, prevY);
        jumps.lineTo(x, y);
      }
      prevStitch = false;
    } else if (c === CMD.TRIM) {
      trims.push({ x, y });
      prevStitch = false;
    } else if (c === CMD.COLOR_CHANGE) {
      colorChanges.push({ x, y });
      prevStitch = false;
    }
    prevX = x;
    prevY = y;
    started = true;
  }
  return { blocks, jumps, trims, colorChanges };
}

export function setViewTransform(ctx: CanvasRenderingContext2D, v: Viewport, dpr: number): void {
  ctx.setTransform(v.zoom * dpr, 0, 0, v.zoom * dpr, v.panX * dpr, v.panY * dpr);
}

export interface DrawOptions {
  showJumps: boolean;
  /** 0..1 */
  opacity?: number;
}

export function drawStitchPaths(ctx: CanvasRenderingContext2D, p: StitchPaths, v: Viewport, dpr: number, o: DrawOptions): void {
  setViewTransform(ctx, v, dpr);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalAlpha = o.opacity ?? 1;
  // keep threads at least ~1 device pixel wide when zoomed out
  const w = Math.max(THREAD_WIDTH_MM, 1 / v.zoom);
  for (const b of p.blocks) {
    ctx.strokeStyle = b.color;
    ctx.lineWidth = w;
    ctx.stroke(b.path);
  }
  if (v.zoom > 6) {
    // thread sheen: a thin light line on top
    ctx.globalAlpha = 0.18 * (o.opacity ?? 1);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = w * 0.3;
    for (const b of p.blocks) ctx.stroke(b.path);
  }
  ctx.globalAlpha = 1;
  if (o.showJumps) {
    ctx.setLineDash([4 / v.zoom, 3 / v.zoom]);
    ctx.strokeStyle = '#d62828';
    ctx.lineWidth = 1 / v.zoom;
    ctx.stroke(p.jumps);
    ctx.setLineDash([]);
    const r = 3 / v.zoom;
    ctx.strokeStyle = '#d62828';
    for (const t of p.trims) {
      ctx.beginPath();
      ctx.moveTo(t.x - r, t.y - r);
      ctx.lineTo(t.x + r, t.y + r);
      ctx.moveTo(t.x - r, t.y + r);
      ctx.lineTo(t.x + r, t.y - r);
      ctx.stroke();
    }
  }
}

export function clearCanvas(ctx: CanvasRenderingContext2D, w: number, h: number, color: string): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, w, h);
}

/** Draws a millimetre grid (10 mm major lines) in the visible area. */
export function drawGrid(ctx: CanvasRenderingContext2D, v: Viewport, dpr: number, cssW: number, cssH: number): void {
  const minor = v.zoom >= 6 ? 1 : v.zoom >= 1.5 ? 5 : 10;
  const x0 = Math.floor(-v.panX / v.zoom / minor) * minor;
  const y0 = Math.floor(-v.panY / v.zoom / minor) * minor;
  const x1 = (cssW - v.panX) / v.zoom;
  const y1 = (cssH - v.panY) / v.zoom;
  setViewTransform(ctx, v, dpr);
  ctx.lineWidth = 1 / v.zoom;
  for (let pass = 0; pass < 2; pass++) {
    ctx.strokeStyle = pass === 0 ? '#0000000d' : '#0000001f';
    ctx.beginPath();
    for (let x = x0; x <= x1; x += minor) {
      const major = Math.round(x) % 10 === 0;
      if ((pass === 1) !== major) continue;
      ctx.moveTo(x, y0);
      ctx.lineTo(x, y1);
    }
    for (let y = y0; y <= y1; y += minor) {
      const major = Math.round(y) % 10 === 0;
      if ((pass === 1) !== major) continue;
      ctx.moveTo(x0, y);
      ctx.lineTo(x1, y);
    }
    ctx.stroke();
  }
}

/** Small PNG preview of a sequence (for the project list). Returns null outside the browser. */
export function renderThumbnail(seq: StitchSequence, w: number, h: number): string | null {
  if (typeof document === 'undefined' || typeof Path2D === 'undefined') return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < seq.count; i++) {
    if (seq.cmds[i] !== CMD.STITCH) continue;
    minX = Math.min(minX, seq.xs[i]);
    maxX = Math.max(maxX, seq.xs[i]);
    minY = Math.min(minY, seq.ys[i]);
    maxY = Math.max(maxY, seq.ys[i]);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  clearCanvas(ctx, w, h, FABRIC);
  if (maxX > minX) {
    const zoom = Math.min((w - 16) / (maxX - minX), (h - 16) / (maxY - minY));
    const v = { zoom, panX: w / 2 - ((minX + maxX) / 2) * zoom, panY: h / 2 - ((minY + maxY) / 2) * zoom };
    drawStitchPaths(ctx, buildStitchPaths(seq), v, 1, { showJumps: false });
  }
  return canvas.toDataURL('image/png');
}
